import React from "react";
import Link from "next/link";
import type { Blocks } from "@/types/cms";

// URLs that start with "/" (and aren't protocol-relative "//...") are treated
// as internal so they use Next's client-side router instead of full-page
// loads. Prevents cross-tab navigation that breaks the first-visit
// `PageLoader` gate.
const isInternalUrl = (url: string | undefined | null): boolean => {
  if (!url) return false;
  const trimmed = url.trim();
  return trimmed.startsWith("/") && !trimmed.startsWith("//");
};

export function getFirstHeadingFromBlocks(
  blocks: Blocks | undefined
): string | undefined {
  if (!Array.isArray(blocks)) return undefined;
  for (const block of blocks as any[]) {
    if (block?.type === "heading" && Array.isArray(block.children)) {
      const text = block.children
        .map((child: any) =>
          typeof child.text === "string" ? child.text : ""
        )
        .join("")
        .trim();
      if (text) return text;
    }
  }
  return undefined;
}

export function getFirstParagraphFromBlocks(
  blocks: Blocks | undefined
): string | undefined {
  if (!Array.isArray(blocks)) return undefined;
  for (const block of blocks as any[]) {
    if (block?.type === "paragraph" && Array.isArray(block.children)) {
      const text = block.children
        .map((child: any) =>
          typeof child.text === "string" ? child.text : ""
        )
        .join("")
        .trim();
      if (text) return text;
    }
  }
  return undefined;
}

function getAlignmentClass(textAlign?: string): string {
  const val = (textAlign || "").toLowerCase();
  if (val === "center") return "align-center";
  if (val === "right") return "align-right";
  if (val === "justify") return "align-justify";
  return "align-left";
}

type RichTextAlign = "left" | "center" | "right" | "justify";

function renderInlineLeaves(nodes: any[], accentClass?: string): React.ReactNode {
  if (!Array.isArray(nodes)) return null;
  return nodes.map((child, i) => {
    if (!child || typeof child !== "object") return null;

    let content: React.ReactNode = child.text ?? "";

    if (child.bold) content = <strong>{content}</strong>;
    if (child.italic) {
      content = accentClass ? (
        <span className={accentClass}>{content}</span>
      ) : (
        <em>{content}</em>
      );
    }
    if (child.underline) content = <u>{content}</u>;

    if (child.type === "link" && child.url) {
      const linkText =
        (Array.isArray(child.children)
          ? child.children.map((c: any) => c.text ?? "").join("")
          : child.text) || "";
      content = isInternalUrl(child.url) ? (
        <Link href={child.url} style={{ whiteSpace: "pre-wrap" }}>
          {linkText}
        </Link>
      ) : (
        <a
          href={child.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ whiteSpace: "pre-wrap" }}
        >
          {linkText}
        </a>
      );
    }

    const className = child.strikethrough ? "muted" : undefined;

    return (
      <span
        key={i}
        className={className}
        style={{ whiteSpace: "pre-wrap" }}
      >
        {content}
      </span>
    );
  });
}

export function renderRichTextWithMutedStrikethrough(
  blocks: Blocks | undefined,
  options?: { defaultAlign?: RichTextAlign; headingVariant?: "title" | "accent" }
): React.ReactNode {
  if (!Array.isArray(blocks)) return null;

  const asArray = blocks as any[];

  return asArray.map((block, index) => {
    if (!block) return null;

    const children = Array.isArray(block.children) ? block.children : [];
    const isLast = index === asArray.length - 1;

    const renderInline = (nodes: any[], accentClass?: string) =>
      renderInlineLeaves(nodes, accentClass);

    switch (block.type) {
      case "heading": {
        const headingAlign = getAlignmentClass(
          (block.textAlign as string | undefined) ?? options?.defaultAlign
        );
        const level = block.level || 2;
        const clamped = Math.min(Math.max(level, 1), 6);
        const TitleTag = (["h1", "h2", "h3", "h4", "h5", "h6"] as const)[
          clamped - 1
        ];
        const titleClass =
          clamped === 1
            ? "title-2xl"
            : clamped === 2
            ? "title-xl"
            : clamped === 3
            ? "title-lg"
            : clamped === 4
            ? "title-md"
            : clamped === 5
            ? "title-sm"
            : "title-xs";
        const accentClass =
          clamped === 1
            ? "accent-2xl"
            : clamped === 2
            ? "accent-xl"
            : clamped === 3
            ? "accent-lg"
            : clamped === 4
            ? "accent-md"
            : clamped === 5
            ? "accent-sm"
            : "accent-xs";

        const headingClass =
          options?.headingVariant === "accent" ? accentClass : titleClass;

        const marginBottom = isLast
          ? 0
          : clamped <= 2
          ? 16
          : clamped <= 4
          ? 12
          : 8;

        return (
          <TitleTag
            key={index}
            className={`${headingClass} ${headingAlign}`}
            style={{ marginBottom, whiteSpace: "pre-wrap" }}
          >
            {renderInline(children, accentClass)}
          </TitleTag>
        );
      }
      case "paragraph": {
        const paragraphAlign = getAlignmentClass(
          (block.textAlign as string | undefined) ?? options?.defaultAlign
        );
        const paragraphMargin = isLast ? 0 : 16;
        return (
          <p
            key={index}
            className={`body-md muted ${paragraphAlign}`}
            style={{ marginBottom: paragraphMargin, whiteSpace: "pre-wrap" }}
          >
            {renderInline(children)}
          </p>
        );
      }
      case "list":
      case "bullet-list":
      case "ordered-list": {
        const isOrdered =
          block.format === "ordered" || block.type === "ordered-list";
        const ListTag = isOrdered ? "ol" : "ul";
        const listItems = block.children || block.items || [];
        return (
          <ListTag key={index} style={{ marginBottom: isLast ? 0 : 16 }}>
            {Array.isArray(listItems)
              ? listItems.map((item: any, liIndex: number) => (
                  <li key={liIndex}>{renderInline(item.children || [])}</li>
                ))
              : null}
          </ListTag>
        );
      }
      default:
        // Fallback: render children inline in a paragraph
        return (
          <p
            key={index}
            className="body-md align-left"
            style={{ whiteSpace: "pre-wrap" }}
          >
            {renderInline(children)}
          </p>
        );
    }
  });
}

const LIST_TYPES = new Set(["list", "bullet-list", "ordered-list"]);

type PriceFeature = { title: any[]; subtitles: any[][]; key: string };

function pairAsTitleSubtitle(items: PriceFeature[]): PriceFeature[] {
  const paired: PriceFeature[] = [];
  for (let i = 0; i < items.length; i += 2) {
    const titleItem = items[i];
    const subtitleItem = items[i + 1];
    if (subtitleItem) {
      paired.push({
        title: titleItem.title,
        subtitles: [subtitleItem.title],
        key: titleItem.key,
      });
    } else {
      paired.push(titleItem);
    }
  }
  return paired;
}

export function renderPriceFeatures(
  blocks: Blocks | undefined,
  classNames: {
    feature: string;
    featureTitle: string;
    featureSubtitle: string;
    featureGroup: string;
  }
): React.ReactNode {
  if (!Array.isArray(blocks)) return null;

  const getListItems = (listBlock: any): any[] => {
    const items = listBlock?.children ?? listBlock?.items;
    return Array.isArray(items) ? items : [];
  };

  const output: React.ReactNode[] = [];

  (blocks as any[]).forEach((block, blockIndex) => {
    if (!block) return;

    if (LIST_TYPES.has(block.type)) {
      // legacy CMS Blocks represents nested bullets as sibling `list` children
      // (with indentLevel > 0) following a `list-item`, rather than as true
      // children of the list-item. Walk siblings and group each list-item with
      // the indented list(s) that follow it.
      const childrenArr: any[] = Array.isArray(block.children)
        ? block.children
        : [];

      type Feature = { title: any[]; subtitles: any[][]; key: string };
      const features: Feature[] = [];
      let current: Feature | null = null;

      const attachNestedList = (nestedList: any, feature: Feature | null) => {
        if (!feature) return;
        getListItems(nestedList).forEach((ni: any) => {
          const leaves = Array.isArray(ni?.children)
            ? ni.children.filter((c: any) => c && !LIST_TYPES.has(c.type))
            : [];
          feature.subtitles.push(leaves);
        });
      };

      for (let i = 0; i < childrenArr.length; i++) {
        const child = childrenArr[i];
        if (!child) continue;

        if (child.type === "list-item") {
          if (current) features.push(current);
          const liChildren: any[] = Array.isArray(child.children)
            ? child.children
            : [];
          const titleLeaves = liChildren.filter(
            (c: any) => c && !LIST_TYPES.has(c.type)
          );
          const nestedInside = liChildren.filter(
            (c: any) => c && LIST_TYPES.has(c.type)
          );
          current = {
            title: titleLeaves,
            subtitles: [],
            key: `${blockIndex}-${i}`,
          };
          nestedInside.forEach((nl: any) => attachNestedList(nl, current));
        } else if (LIST_TYPES.has(child.type)) {
          attachNestedList(child, current);
        }
      }
      if (current) features.push(current);

      // Pair flat items as title → subtitle when the editor didn't produce any
      // nested lists (Lexical writes every list-item as a flat sibling, so this
      // is the common case). Keeps the "bold feature + muted description"
      // layout without requiring authors to manually indent.
      const noNestedLists = features.every((f) => f.subtitles.length === 0);
      const displayFeatures = noNestedLists && features.length >= 2
        ? pairAsTitleSubtitle(features)
        : features;

      output.push(
        <div key={`g-${blockIndex}`} className={classNames.featureGroup}>
          {displayFeatures.map((f) => (
            <div key={f.key} className={classNames.feature}>
              <div className={`${classNames.featureTitle} title-sm`}>
                {renderInlineLeaves(f.title)}
              </div>
              {f.subtitles.map((sub, si) => (
                <div
                  key={si}
                  className={`${classNames.featureSubtitle} body-xs`}
                >
                  {renderInlineLeaves(sub)}
                </div>
              ))}
            </div>
          ))}
        </div>
      );
      return;
    }

    if (block.type === "paragraph") {
      const children = Array.isArray(block.children) ? block.children : [];
      output.push(
        <p
          key={`p-${blockIndex}`}
          className="body-sm"
          style={{ whiteSpace: "pre-wrap", color: "var(--foreground)" }}
        >
          {renderInlineLeaves(children)}
        </p>
      );
      return;
    }

    // Fallback for any other block types (headings, etc.).
    output.push(
      <React.Fragment key={`b-${blockIndex}`}>
        {renderRichTextWithMutedStrikethrough([block])}
      </React.Fragment>
    );
  });

  return output;
}

